import { useEffect, useMemo, useState } from "react";
import { getParts } from "../../../../utils/helpers";
import { OptionCard } from "./OptionCard";
import { RenderPart } from "./RenderPart";
import { m } from "framer-motion";
import useSound from "use-sound";
import { analytics } from "@services/analytics";
import { useRouter } from "next/router";
import { CustomButton } from "../../../set/components";
import DraggableList from "@modules/shared/components/DraggableList";
import isEqual from "lodash/isEqual";

const OptionsCardContainer = ({ children, className }) => {
    return (
        <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            className={className}
        >
            {children}
        </m.div>
    );
};
interface QuestionCardProps {
    content: any;
    handleAttempt: any;
    hiddenOptions: any;
    timer: any;
    duration: any;
    handleNext: any;
    questionOnly: any;
    stopCountdown: any;
    submitButtonCta?: string;
}

export const QuestionCard: React.FC<QuestionCardProps> = (props) => {
    const [playOptionSelected] = useSound(
        "/assets/sounds/Select Sound_minimal-beep-alert-2-SBA-300463242.mp3",
    );
    const [playCorrectAnswerSound] = useSound(
        "/assets/sounds/Correct Answer_happy-award-achievement-SBA-300418984.mp3",
        {
            forceSoundEnabled: true,
        },
    );
    const [playIncorrectAnswerSound] = useSound(
        "/assets/sounds/Incorrect-Answer-.mp3",
        {
            forceSoundEnabled: true,
        },
    );
    const [playNotAttemptedSound] = useSound(
        "/assets/sounds/No Attempt_elearning-incorrect-answer-sound-1-SBA-300286572.mp3",
        {
            forceSoundEnabled: true,
        },
    );

    const {
        content,
        handleAttempt,
        hiddenOptions,
        timer,
        duration,
        handleNext,
        questionOnly = false,
        stopCountdown,
        baseAnalyticsEvent,
        isCorrect,
        isSpeedy,
        submitButtonCta = "",
        set,
        handleNoAttempt,
        headerRc,
    } = props;
    const { question, type, re_order_question, isCaseStudy } = content;
    const { text, options, correct_option, correct_order } =
        question || re_order_question || {};
    const [selected, setSelected] = useState(-1);
    const [reOrderSubmitted, setReOrderSubmitted] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(
        [...Array(options.length)].map((i, idx) => idx),
    );
    const [showAnswers, setShow] = useState(false);
    const { query, isReady } = useRouter();
    const shouldPauseTimer = query.pauseTimer == "true";
    const getSelectedOrder = () => selectedOrder.map((o) => String(o + 1));

    function postReOrderSubmitCallback(isReOrderCorrect: boolean) {
        if (!showAnswers) {
            setShow(true);
            if (isReOrderCorrect) playCorrectAnswerSound();
            else playIncorrectAnswerSound();
        }
    }

    useEffect(() => {
        if (!questionOnly) {
            const isReOrderCorrect = isEqual(
                getSelectedOrder(),
                correct_order?.slice(0, options?.length),
            );

            const hasOrderChanged = !isEqual(
                getSelectedOrder(),
                [...Array(options.length)].map((i, idx) => String(idx + 1)),
            );
            const shouldAttempt = isReOrderCorrect || hasOrderChanged;
            if (timer == duration && shouldAttempt && !reOrderSubmitted) {
                handleReOrderAttempt();
                return;
            }

            if (selected >= 0) {
                setTimeout(() => {
                    if (!showAnswers) {
                        setShow(true);
                        if (
                            shuffledOptions[selected] ==
                            options[correct_option - 1]
                        )
                            playCorrectAnswerSound();
                        else playIncorrectAnswerSound();
                    }
                }, 500);
            }

            // negation of no-attempt for re-order question
            if (reOrderSubmitted) {
                setTimeout(() => {
                    postReOrderSubmitCallback(isReOrderCorrect);
                }, 500);
            }

            if (timer == duration) {
                playNotAttemptedSound();
                setTimeout(() => {
                    !shouldPauseTimer &&
                        handleNext({ forceNextQuestion: !reOrderSubmitted });

                    if (!(type == "re_order" && shouldAttempt))
                        handleNoAttempt();
                }, 1000);
            }

            let timeout =
                shuffledOptions[selected] == options[correct_option - 1]
                    ? 2000
                    : 5000;
            if (type == "re_order") {
                timeout = isReOrderCorrect ? 5000 : 10000;
            }

            if (showAnswers)
                setTimeout(() => {
                    !shouldPauseTimer &&
                        handleNext({ forceNextQuestion: type == "re_order" });
                }, timeout);
        }
    }, [selected, timer, showAnswers, reOrderSubmitted]);

    const shuffledOptions = useMemo(() => {
        return options
            .map((value) => ({ value, sort: Math.random() }))
            .sort((a, b) => a.sort - b.sort)
            .map(({ value }) => value);
    }, [options]);

    const getCorrect = (option: any, index: number) => {
        if (!showAnswers) return false;
        let isCorrect = options[correct_option - 1] == option ? true : false;

        if (type == "re_order") {
            if (index < correct_order.length || index < selectedOrder.length)
                isCorrect = correct_order[index] - 1 == selectedOrder[index];
        }
        return isCorrect;
    };

    const renderOption = (option: string, index: number, isDrag = false) => (
        <OptionCard
            src={option}
            key={`opt_${index}`}
            correct={getCorrect(option, index)}
            review={
                showAnswers &&
                (selected == index ||
                    (options[correct_option - 1] == option &&
                        type !== "primary_question") ||
                    reOrderSubmitted)
            }
            selected={selected == index}
            onClick={async () => {
                if (isDrag) return;
                if (selected < 0) {
                    await analytics.track("question_attempted", {
                        ...(set.type == "practise_learn_practise"
                            ? {
                                  question_type: type,
                              }
                            : {}),
                        ...baseAnalyticsEvent,
                        isCorrect,
                        isSpeedy,
                    });
                    stopCountdown();
                    playOptionSelected();
                    setSelected(index);
                    handleAttempt({ option, index });
                }
            }}
        />
    );

    const draggableListItemBuilder = (option, index) =>
        renderOption(option, index, true);

    const handleReOrderAttempt = async () => {
        if (selectedOrder.length > 0) {
            await analytics.track("question_attempted", {
                ...(set.type == "practise_learn_practise"
                    ? {
                          question_type: type,
                      }
                    : {}),
                ...baseAnalyticsEvent,
                isCorrect,
                isSpeedy,
            });
            stopCountdown();
            playOptionSelected();
            setReOrderSubmitted(true);
            handleAttempt({
                option: getSelectedOrder(),
                index: null,
            });
        }
    };

    const className = "px-8";
    return (
        <div className={"pt-20 pb-4"}>
            <div className={className}>
                {
                    headerRc[
                        type === "question"
                            ? isCaseStudy
                                ? "case_study"
                                : "mcq"
                            : "re_order"
                    ]
                }
            </div>
            <div className={className}>
                {getParts(text).map((part, pIndex) => (
                    <RenderPart part={part} key={pIndex} />
                ))}
            </div>

            {type == "question" && (
                <OptionsCardContainer className="flex flex-col">
                    {!questionOnly &&
                        shuffledOptions.map((option: string, index: number) =>
                            hiddenOptions.includes(option) ? null : (
                                <div className={className}>
                                    {renderOption(option, index)}
                                </div>
                            ),
                        )}
                </OptionsCardContainer>
            )}

            {type == "re_order" && !questionOnly && (
                <>
                    <OptionsCardContainer className="flex flex-col">
                        <DraggableList
                            disabled={showAnswers}
                            items={options}
                            reset={
                                showAnswers
                                    ? correct_order
                                          .slice(0, options.length)
                                          .map((o) => o - 1)
                                    : null
                            }
                            itemBuilder={draggableListItemBuilder}
                            onSortEnd={(order: number[]) =>
                                setSelectedOrder(order)
                            }
                        />
                        {!showAnswers && (
                            <div className={className}>
                                <br />
                                <CustomButton
                                    cta={"Submit"}
                                    type={"action"}
                                    onClick={handleReOrderAttempt}
                                />
                            </div>
                        )}
                    </OptionsCardContainer>
                </>
            )}

            {submitButtonCta && (
                <div className="mt-16 mb-4 ">
                    <CustomButton cta={submitButtonCta} displayType="gray" />
                </div>
            )}
        </div>
    );
};
